import React, { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import Icon from '@components/Icon';
import ItemPaymentModal from '@organisms/modals/payment/ItemPaymentModal';
import { snackbarVar } from '@lib/apollo/policies';
import InfoIcon from '@assets/InfoOutlineIcon';
import AnalyticsManager from '@lib/analytics/manager';
import LanguageContext from '@lib/contexts/languageContext';
import { UserContext } from '@lib/contexts/UserProvider';
import { Box, Flex } from '@components/layout/Grid';
import IconTooltip from '@components/IconTooltip';
import RegisterFormTemplate from '@ecosystems/registerForm.pageTemplate';
import {
  useParticipateInAnEventMutation,
  ItemKind,
  useActivateLiveEventMutation,
  CodeStatusKind,
  ValidateCode,
  LiveEventKind,
  LiveEventQuery,
  LiveEventPaymentKind,
} from '@gql/generated';
import Banner from '@organisms/Banner';
import { EventPaymentAmount } from './EventPaymentAmount';
import { EventFlexiblePaymentAmount } from './EventFlexiblePaymentAmount';
import { Button } from '@components/ButtonV2';
import useFlags from '@lib/hooks/useFlags';
import { Checkbox } from 'src/ui/checkbox';
import Link from 'next/link';
import { StartTrialSubscriptionButton } from '@ecosystems/buttons/StartTrialSubscriptionButton';

const Paragraph = RegisterFormTemplate.Paragraph;

const OfferMessage = styled(Paragraph)`
  font-size: 13px;
  color: #242424;
  font-weight: 500;
  border-radius: 3px;
  padding: 15px;
  margin-top: 20px;
  background-color: #fbedf3;
`;

const Tooltip = styled(IconTooltip)`
  position: relative;
  display: flex;
  align-items: center;
`;

const lockIconStyle = css`
  margin-right: 10px;
`;

const Next = styled(Button)`
  display: flex;
  align-items: center;
`;

const NoticeBanner = styled(Banner)`
  flex: unset;
  align-items: start;
  margin: 20px 0;
`;

const Separator = styled.div`
  height: 1px;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.15);
`;

const Actions = styled(Flex)`
  position: sticky;
  bottom: 0;
  gap: 10px;
  width: calc(100% + 20px);
  padding: 20px 0;
  gap: 10px;
  margin-left: -10px;
  background-color: white;

  button {
    --px: 16px;
  }
  a {
    --px: 18px;
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    position: static;
    width: 100%;
    margin: 0;

    button {
      --px: 24px;
    }
    a {
      --px: 24px;
    }
  }
`;

type LiveEventPaymentSectionProps = {
  event: Pick<
    LiveEventQuery['liveEvent'],
    | 'id'
    | 'slug'
    | 'title'
    | 'kind'
    | 'price'
    | 'themeLabel'
    | 'primaryInstructor'
    | 'paymentKind'
    | 'flexibleMaxAmountCents'
    | 'flexibleMinAmountCents'
    | 'userAccess'
    | 'isUserEligible'
  >;
  accessCodeState: Partial<ValidateCode>;
  onLiveEventReserved(): void;
};

export const EventPaymentSection = (props: LiveEventPaymentSectionProps) => {
  const { event, accessCodeState, onLiveEventReserved } = props;
  const router = useRouter();
  const lang = useContext(LanguageContext);
  const [flexiblePrice, setFlexiblePrice] = useState(null);
  const [flags] = useFlags();
  const [currentUser, { refetch: refetchUser }] = useContext(UserContext);

  const slug = event?.slug;

  let amountToPay;
  if (event.paymentKind === LiveEventPaymentKind['Flexible']) {
    amountToPay = flexiblePrice;
  } else {
    amountToPay =
      (event.price?.discountApplied
        ? event.price?.discountedPrice
        : event.price?.originalPrice) / 100;
  }

  const verification_required =
    flags.free_live_verification_required === false
      ? false
      : !currentUser?.isEmailActivated;

  const [activateLiveEventMutation, { loading: activationLoading }] =
    useActivateLiveEventMutation({
      onCompleted(response) {
        if (response.activateLiveEvent.success) {
          if (accessCodeState.status === CodeStatusKind['Valid']) {
            AnalyticsManager().sendUsedLiveEventCode({
              name: event.title,
              theme: event.themeLabel,
              currency: event.price?.currency,
              amount: event.price?.originalPrice,
              instructor: event.primaryInstructor?.name,
              code: accessCodeState.code,
            });
          }

          refetchUser();
          snackbarVar({
            isSnackBarOpen: true,
            snackBarMessage: t({
              id: 'live.payment_page.event_accesscode_activated.toast',
            }),
          });
          onLiveEventReserved();
        }
      },
    });

  const [reserveFreeEvent, { loading: reserveEventLoading }] =
    useParticipateInAnEventMutation({
      onCompleted(response) {
        if (response.participateInAnEvent.success) {
          refetchUser();
          snackbarVar({
            isSnackBarOpen: true,
            snackBarMessage: t({
              id: 'live.payment_page.free_event_reserved.toast',
            }),
          });
          onLiveEventReserved();
          AnalyticsManager().sendJoinedLiveEvent({
            name: event.title,
            theme: event.themeLabel,
            value: event.price?.originalPrice,
            currency: event.price?.currency,
            instructor: event.primaryInstructor?.name,
          });
        }
      },
    });

  const validateForm = (callback) => {
    const form = document.forms['form1'] as HTMLFormElement;
    if (form) {
      if (form.checkValidity()) {
        callback();
      } else {
        form.reportValidity();
      }
    } else {
      callback();
    }
  };

  const reserveEventSpot = () => {
    reserveFreeEvent({ variables: { slug: event.slug } });
  };

  const sendPaymentStartEvent = () => {
    //
    AnalyticsManager().sendLivePaymentBeginEvent({
      id: event?.id,
      title: event?.title,
      slug: event?.slug,
      value: amountToPay,
      currency: event?.price?.currency,
      instructor_name: event?.primaryInstructor?.name,
      event_theme: event?.themeLabel,
    });
  };

  const renderActionButton = () => {
    const freeEvent =
      event?.price?.originalPrice === 0 || event.price?.discountedPrice === 0;

    if (
      event.paymentKind !== LiveEventPaymentKind['CampaignLive'] &&
      freeEvent
    ) {
      return (
        <Next
          appearance="dark-blue"
          onClick={() => reserveEventSpot()}
          loading={reserveEventLoading}
          disabled={verification_required}
        >
          <Trans id="live.payment_page.free_reserve_spot" />
        </Next>
      );
    } else if (
      accessCodeState?.status === CodeStatusKind['Valid'] ||
      (flexiblePrice === 0 && event?.flexibleMinAmountCents >= 0) ||
      event.paymentKind === LiveEventPaymentKind['PremiumLive']
    ) {
      let disabled = true;

      if (
        accessCodeState?.status === CodeStatusKind['Valid'] ||
        (flexiblePrice === 0 && event?.flexibleMinAmountCents >= 0)
      ) {
        disabled = false;
      } else if (
        event.paymentKind === LiveEventPaymentKind['PremiumLive'] &&
        event.isUserEligible
      ) {
        disabled = false;
      }

      return (
        <Next
          appearance="dark-blue"
          disabled={disabled}
          loading={activationLoading}
          onClick={() => {
            if (accessCodeState?.status === CodeStatusKind['Valid']) {
              activateLiveEventMutation({
                variables: {
                  code: accessCodeState?.code,
                  slug: event.slug,
                },
              });
            } else {
              reserveFreeEvent({ variables: { slug: event.slug } });
            }
          }}
        >
          <Trans id="live.payment_page.action_button.activate" />
        </Next>
      );
      //
    } else if (
      event.paymentKind === LiveEventPaymentKind['CampaignLive'] ||
      event.kind === LiveEventKind['DonationClass']
    ) {
      return (
        <Next appearance="dark-blue" disabled>
          <Trans id="live.payment_page.action_button.activate" />
        </Next>
      );
    } else {
      return (
        <ItemPaymentModal
          slug={event.slug}
          event_title={event.title}
          successPageURL={`${process.env.DOMAIN}/${lang}/live/${slug}/success`}
          failPageURL={`${process.env.DOMAIN}/${lang}/live/${slug}/fail`}
          currency={event.price?.currency}
          itemKind={ItemKind['LiveEvent']}
          amount={amountToPay}
          sourcePage="live"
          flexiblePriceAmount={
            event?.paymentKind === LiveEventPaymentKind['Flexible']
              ? amountToPay
              : null
          }
          onPaymentComplete={({ orderId }) => {
            // this will only be called on adyen success payment
            router.push({
              pathname: `/${lang}/live/${slug}/success`,
              query: {
                email: currentUser.email,
                order_id: orderId,
              },
            });
          }}
        >
          {(open) => {
            return (
              <Next
                appearance="dark-blue"
                disabled={
                  !!event.userAccess ||
                  event?.paymentKind === LiveEventPaymentKind['Flexible']
                    ? !(
                        event?.flexibleMaxAmountCents / 100 >= flexiblePrice &&
                        event?.flexibleMinAmountCents / 100 <= flexiblePrice
                      )
                    : false
                }
                onClick={() => {
                  validateForm(() => {
                    open();
                    sendPaymentStartEvent();
                  });
                }}
              >
                <Icon
                  name="ygb-icon-programlock-ic"
                  fontSize="16px"
                  customStyle={lockIconStyle}
                />
                <Trans id="live.payment_page.payment_reserve_spot" />
              </Next>
            );
          }}
        </ItemPaymentModal>
      );
    }
  };

  let content = null,
    showPrice = accessCodeState?.status !== CodeStatusKind['Valid'];

  if (
    showPrice &&
    event?.price?.originalPrice &&
    event?.price?.originalPrice >= 0
  ) {
    content = (
      <EventPaymentAmount
        liveEvent={event}
        codeStatus={accessCodeState?.status}
      />
    );
  } else if (
    event?.paymentKind === LiveEventPaymentKind['Flexible'] &&
    accessCodeState?.status !== CodeStatusKind['Valid']
  ) {
    content = (
      <Flex flexDirection="column" my={20}>
        <form
          onChange={(e) => {
            const target = e.target as HTMLInputElement;
            if (target.name === 'flexiblePrice') {
              setFlexiblePrice(parseInt(target.value));
            }
          }}
        >
          <EventFlexiblePaymentAmount
            inputName="flexiblePrice"
            currency={event?.price?.currency}
            flexibleMinAmountCents={event?.flexibleMinAmountCents}
            flexibleMaxAmountCents={event?.flexibleMaxAmountCents}
            flexiblePrice={flexiblePrice}
          />
        </form>
        <Box mt={30} mb={10}>
          <Separator />
        </Box>
        <Box style={{ visibility: flexiblePrice > 0 ? 'visible' : 'hidden' }}>
          <Flex justifyContent="space-between">
            <Paragraph size={16} mb={0}>
              <Trans id="live.payment_page.payment_amount" />:
            </Paragraph>
            <Paragraph size={18} weight={500} mb={0}>
              {flexiblePrice} {event?.price?.currency}
            </Paragraph>
          </Flex>
        </Box>
      </Flex>
    );
  }

  return (
    <>
      {content}
      {
        // has discount
        ((event?.price?.discountedPrice > 0 && event?.price?.discountApplied) ||
          // has price
          event?.price?.originalPrice > 0) &&
        // no access code is present
        accessCodeState?.status !== CodeStatusKind['Valid'] &&
        event.kind !== LiveEventKind['DonationClass'] ? (
          <>
            {!currentUser?.isNoneTrialPremium ? (
              <OfferMessage>
                <Trans
                  id="live.payment_page.discount_text"
                  components={[
                    <Button
                      key="0"
                      href={{
                        pathname: `/${lang}/subscription/select`,
                        query: { to: router.asPath },
                      }}
                      link
                    >
                      0
                    </Button>,
                  ]}
                />
              </OfferMessage>
            ) : null}
            {showPrice &&
            ((event?.price?.discountApplied &&
              event?.price?.discountedPrice > 0) ||
              event.price?.originalPrice > 0) ? (
              <form name="form1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="agree_to_terms"
                    name="agree_to_terms"
                    required
                  />
                  <Paragraph
                    size={13}
                    // for is not a valid p tag attribute
                    {...{ as: 'label', htmlFor: 'agree_to_terms' }}
                  >
                    <Trans
                      id="live.payment_page.term_conditions"
                      components={[
                        <a
                          key="0"
                          className="text-primary font-semibold"
                          href={
                            lang === 'se'
                              ? '/se/villkor'
                              : '/en/terms-and-conditions'
                          }
                          target="_blank"
                          rel="noreferrer"
                        ></a>,
                      ]}
                    />
                  </Paragraph>
                  <Tooltip icon={<InfoIcon className="h-4" />}>
                    <Trans
                      id="live.payment_page.term_conditions_tooltip"
                      components={[<a href="#" key="0"></a>]}
                    />
                  </Tooltip>
                </div>
              </form>
            ) : null}
          </>
        ) : null
      }
      {verification_required &&
      event?.paymentKind === LiveEventPaymentKind['FreeForAll'] ? (
        <NoticeBanner appearance="warning">
          <Trans id="live.payment_page.activate_email_banner" />
        </NoticeBanner>
      ) : null}
      {event.paymentKind === LiveEventPaymentKind['CampaignLive'] ? (
        <NoticeBanner appearance="warning">
          <Trans id="live.event_page.live_campaign_banner" />
        </NoticeBanner>
      ) : null}
      <Actions mt={20}>{renderActionButton()}</Actions>
    </>
  );
};
